// -----------------------------------------------------------------------------
// generateComposerStorage
// -----------------------------------------------------------------------------
// Session-scoped, workspace-scoped persistence for the Generate Post composer's
// UNFINISHED input state (topic / goal / tone / custom tone / platforms /
// image source). Kept fully independent from saved drafts:
//
//   - Storage:   sessionStorage (cleared on browser tab close AND on logout).
//   - Key:       postmoro_generate_composer::<workspace_id>
//                (workspace_id === "__primary__" when no explicit workspace
//                header is set — matches how api.js treats a null active
//                workspace id).
//   - Scope:     ONLY lightweight composer inputs. NEVER File / Blob objects,
//                NEVER dataURL images (5 MB quota), NEVER server-owned draft
//                fields like caption / hashtags / result / post_id.
//   - Precedence: The existing saved-draft banner + `continueDraft` /
//                `startNewGeneration` flows remain fully authoritative; this
//                module is a pure UX convenience for in-progress typing.
// -----------------------------------------------------------------------------

export const COMPOSER_KEY_PREFIX = "postmoro_generate_composer::";
export const COMPOSER_SCHEMA_VERSION = 1;

function keyFor(workspaceId) {
  const ws = workspaceId || "__primary__";
  return `${COMPOSER_KEY_PREFIX}${ws}`;
}

function safeSession() {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage || null;
  } catch (_e) {
    return null;
  }
}

export function readStoredComposer(workspaceId) {
  const store = safeSession();
  if (!store) return null;
  try {
    const raw = store.getItem(keyFor(workspaceId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.v !== COMPOSER_SCHEMA_VERSION) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

export function writeStoredComposer(workspaceId, payload) {
  const store = safeSession();
  if (!store) return;
  try {
    const body = JSON.stringify({ v: COMPOSER_SCHEMA_VERSION, ...(payload || {}) });
    store.setItem(keyFor(workspaceId), body);
  } catch (_e) {
    // Quota / disabled — silently drop; typing UX still works, just no persist.
  }
}

export function clearStoredComposer(workspaceId) {
  const store = safeSession();
  if (!store) return;
  try {
    store.removeItem(keyFor(workspaceId));
  } catch (_e) {
    /* ignore */
  }
}

// Called from logout — wipes composer state for ALL workspaces the user
// touched during this session (they may have switched workspaces before
// logging out). Only removes keys owned by this module; unrelated keys
// are untouched.
export function clearAllStoredComposers() {
  const store = safeSession();
  if (!store) return;
  try {
    const victims = [];
    for (let i = 0; i < store.length; i += 1) {
      const k = store.key(i);
      if (k && k.startsWith(COMPOSER_KEY_PREFIX)) victims.push(k);
    }
    victims.forEach((k) => {
      try { store.removeItem(k); } catch (_e) { /* ignore */ }
    });
  } catch (_e) {
    /* ignore */
  }
}
